<script lang="ts">
  import type { SurfaceSnapshot, SurfaceProject } from "../lib/types";
  import { quickAdd } from "../lib/api";
  import { loadSnapshot } from "../lib/store";

  let { data, onclose }: { data: SurfaceSnapshot; onclose: () => void } = $props();

  let text = $state("");
  let projectSlug = $state<string>("__inbox__");
  let account = $state<"personal" | "work">("personal");
  let saving = $state(false);
  let error = $state("");

  function rank(p: SurfaceProject): number {
    return p.priority === "high" ? 0 : p.priority === "medium" ? 1 : 2;
  }
  const projectOptions = $derived(
    [...data.projects]
      .filter((p) => p.status === "active")
      .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title)),
  );

  async function submit(e: Event) {
    e.preventDefault();
    if (!text.trim() || saving) return;
    error = "";
    saving = true;
    try {
      await quickAdd({
        text: text.trim(),
        account,
        project_slug: projectSlug === "__inbox__" ? undefined : projectSlug,
        section: projectSlug === "__inbox__" ? "Tasks" : "Tasks",
      });
      text = "";
      await loadSnapshot({ fresh: true });
      onclose();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      saving = false;
    }
  }

  function backdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) onclose();
  }
</script>

<div
  class="backdrop"
  role="presentation"
  onclick={backdropClick}
  onkeydown={(e) => e.key === "Escape" && onclose()}
>
  <div class="sheet" role="dialog" aria-label="Quick add task">
    <div class="grabber"></div>

    <div class="sheet-head">
      <button type="button" class="cancel" onclick={onclose}>Cancel</button>
      <h2 class="sheet-title">New Task</h2>
      <button
        type="button"
        class="done"
        disabled={!text.trim() || saving}
        onclick={(e) => submit(e as unknown as Event)}
      >
        {saving ? "Adding…" : "Add"}
      </button>
    </div>

    <form onsubmit={submit}>
      <input
        type="text"
        bind:value={text}
        placeholder="What needs doing?"
        autofocus
        autocapitalize="sentences"
        autocorrect="on"
      />

      <div class="grouped-fields">
        <label class="field">
          <span class="field-label">Project</span>
          <select bind:value={projectSlug}>
            <option value="__inbox__">Inbox</option>
            {#each projectOptions as p (p.slug)}
              <option value={p.slug}>
                {p.priority === "high" ? "🔥 " : ""}{p.title}
              </option>
            {/each}
          </select>
        </label>
        <label class="field">
          <span class="field-label">Account</span>
          <select bind:value={account}>
            <option value="personal">Personal</option>
            <option value="work">Work</option>
          </select>
        </label>
      </div>

      {#if error}
        <p class="error">{error}</p>
      {/if}
    </form>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: var(--material-overlay);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    animation: fadeIn 0.18s ease-out;
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .sheet {
    background: var(--bg-elev);
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
    width: 100%;
    max-width: 540px;
    padding: 6px 16px calc(20px + var(--safe-bottom));
    animation: slideUp 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  .grabber {
    width: 36px;
    height: 5px;
    background: rgba(235, 235, 245, 0.25);
    border-radius: 999px;
    margin: 6px auto 6px;
  }

  .sheet-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0 14px;
    gap: 12px;
  }
  .sheet-title {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.005em;
  }
  .cancel {
    color: var(--accent);
    font-size: 17px;
    padding: 6px 4px;
  }
  .done {
    color: var(--accent);
    font-weight: 600;
    font-size: 17px;
    padding: 6px 4px;
  }
  .done:disabled { opacity: 0.4; }

  form { display: flex; flex-direction: column; gap: 14px; }

  input[type="text"] {
    font-size: 17px;
    padding: 14px;
    background: var(--bg-elev-2);
    border: 0;
  }

  .grouped-fields {
    background: var(--bg-elev-2);
    border-radius: 10px;
    overflow: hidden;
  }
  .field {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-bottom: 0.5px solid var(--separator);
  }
  .field:last-child { border-bottom: 0; }
  .field-label {
    font-size: 15px;
    color: var(--fg);
    flex-shrink: 0;
    min-width: 80px;
  }
  .field select {
    flex: 1;
    background: transparent;
    border: 0;
    padding: 0;
    text-align: right;
    color: var(--fg-dim);
    font-size: 15px;
    appearance: none;
  }

  .error { color: var(--priority-high); font-size: 13px; margin: 0; }
</style>
